import React, { useMemo } from 'react';

// Support both snake_case (API) and camelCase formats
interface NineBoxCell {
  xAxisCount?: number;
  yAxisCount?: number;
  xAxisValue?: string;
  yAxisValue?: string;
  cellLabel?: string;
  cellBgColor?: string;
  cellTextColor?: string;
  employeeCount?: number;
  cycleName?: string;
  // snake_case (from API)
  x_axis_count?: number;
  y_axis_count?: number;
  x_axis_value?: string;
  y_axis_value?: string;
  cell_label?: string;
  cell_bg_color?: string;
  cell_text_color?: string;
  employee_count?: number;
  cycle_name?: string;
}

interface AxisLabels {
  xAxisTitle?: string;
  yAxisTitle?: string;
  x1label?: string;
  x2label?: string;
  x3label?: string;
  y1label?: string;
  y2label?: string;
  y3label?: string;
  // snake_case (from API)
  x_axis_title?: string;
  y_axis_title?: string;
}

// Helpers for snake_case/camelCase compatibility
const getXAxisCount = (c: NineBoxCell) => Number(c.xAxisCount ?? c.x_axis_count ?? 1);
const getYAxisCount = (c: NineBoxCell) => Number(c.yAxisCount ?? c.y_axis_count ?? 1);
const getCellLabel = (c: NineBoxCell) => {
  const label = c.cellLabel || c.cell_label || '';
  return label === 'Not Specified' ? '' : label;
};
const getCellBgColor = (c: NineBoxCell) => c.cellBgColor || c.cell_bg_color || '';
const getCellTextColor = (c: NineBoxCell) => c.cellTextColor || c.cell_text_color || '';
const getEmployeeCount = (c: NineBoxCell) => Number(c.employeeCount ?? c.employee_count ?? 0);
const getCycleName = (c: NineBoxCell) => c.cycleName || c.cycle_name || '';

interface CycleOption {
  value: string;
  label: string;
}

interface NineBoxMatrixWidgetProps {
  data?: NineBoxCell[] | { data: NineBoxCell[] };
  axisLabels?: AxisLabels;
  loading?: boolean;
  error?: string;
  onCellClick?: (x: number, y: number, label: string, count: number) => void;
  cycleOptions?: CycleOption[];
  selectedCycleId?: string;
  onCycleChange?: (cycleId: string) => void;
  cycleOptionsLoading?: boolean;
}

// Default cell configuration matching the HTML prototype exactly
// x = performance (1=low, 2=med, 3=high), y = potential (1=low, 2=med, 3=high)
const defaultCellConfig: Record<string, { bg: string; text: string; label: string }> = {
  // Top row (y=3, High Potential)
  '1-3': { bg: 'bg-red-100', text: 'text-red-700', label: 'Risk' },
  '2-3': { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Growth Gem' },
  '3-3': { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Star' },
  // Middle row (y=2, Medium Potential)
  '1-2': { bg: 'bg-red-50', text: 'text-red-600', label: 'Dilemma' },
  '2-2': { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Core Player' },
  '3-2': { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'High Perf' },
  // Bottom row (y=1, Low Potential)
  '1-1': { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Enigma' },
  '2-1': { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Average' },
  '3-1': { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Workhorse' },
};

export const NineBoxMatrixWidget: React.FC<NineBoxMatrixWidgetProps> = ({
  data,
  axisLabels,
  loading,
  error,
  onCellClick,
  cycleOptions,
  selectedCycleId,
  onCycleChange,
  cycleOptionsLoading,
}) => {
  // Handle wrapped data format
  const cellsList: NineBoxCell[] = Array.isArray(data)
    ? data
    : ((data as { data: NineBoxCell[] })?.data || []);

  // Build grid data from API response
  const gridData = useMemo(() => {
    const grid: Record<string, NineBoxCell> = {};

    // Initialize all 9 cells with defaults
    for (let y = 3; y >= 1; y--) {
      for (let x = 1; x <= 3; x++) {
        const key = `${x}-${y}`;
        grid[key] = {
          x_axis_count: x,
          y_axis_count: y,
          employee_count: 0,
          cell_label: defaultCellConfig[key]?.label || '',
        };
      }
    }

    // Overlay API data
    cellsList.forEach(cell => {
      const x = getXAxisCount(cell);
      const y = getYAxisCount(cell);
      const key = `${x}-${y}`;
      if (grid[key]) {
        grid[key] = {
          ...grid[key],
          ...cell,
          employee_count: getEmployeeCount(cell),
        };
      }
    });

    return grid;
  }, [cellsList]);

  // Get axis labels (use short labels like prototype)
  const labels = axisLabels || {};
  const xLabels = [
    labels.x1label || 'Low Perf',
    labels.x2label || 'Med Perf',
    labels.x3label || 'High Perf',
  ];
  const yLabels = [
    labels.y1label || 'Low Pot',
    labels.y2label || 'Med Pot',
    labels.y3label || 'High Pot',
  ];

  const renderHeader = () => (
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-semibold text-gray-900 text-sm">9-Box Talent Matrix</h3>
      {cycleOptions && cycleOptions.length > 0 && onCycleChange && (
        <select
          value={selectedCycleId || ''}
          onChange={(e) => onCycleChange(e.target.value)}
          disabled={cycleOptionsLoading}
          className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 w-[200px] truncate"
        >
          {cycleOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">{error}</div>
      </div>
    );
  }

  // Render a single cell
  const renderCell = (x: number, y: number) => {
    const key = `${x}-${y}`;
    const cell = gridData[key];
    const count = getEmployeeCount(cell);
    const label = getCellLabel(cell) || defaultCellConfig[key]?.label || '';
    const apiBgColor = getCellBgColor(cell);
    const apiTextColor = getCellTextColor(cell);
    const config = defaultCellConfig[key] || { bg: 'bg-gray-100', text: 'text-gray-600' };

    // Always apply Tailwind defaults as base; valid API inline colors override via higher specificity
    // If API returns invalid/empty colors, browser ignores them and Tailwind defaults show through
    const hasApiColors = !!(apiBgColor && apiBgColor !== 'null' && apiTextColor && apiTextColor !== 'null');

    return (
      <div
        key={key}
        onClick={() => onCellClick?.(x, y, label, count)}
        className={`
          flex flex-col items-center justify-center rounded-lg cursor-pointer
          border border-gray-200/50 min-h-[60px]
          transition-all duration-150 hover:shadow-md hover:scale-[1.02]
          ${config.bg}
        `}
        style={hasApiColors ? {
          backgroundColor: apiBgColor,
          color: apiTextColor,
        } : undefined}
      >
        <div className={`text-lg font-bold ${config.text}`}>
          {count}
        </div>
        <div className={`text-[9px] text-gray-500`}>
          {label}
        </div>
      </div>
    );
  };

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      {renderHeader()}

      {/* X-Axis Labels (above grid) */}
      <div className="flex gap-1 mb-1">
        <div className="w-6" /> {/* Spacer for Y-axis label column */}
        {xLabels.map((label, idx) => (
          <div key={idx} className="flex-1 text-center text-[10px] text-gray-400">
            {label}
          </div>
        ))}
      </div>

      {/* Grid with Y-axis labels */}
      <div
        className="flex-1 grid gap-1"
        style={{ gridTemplateColumns: '24px 1fr 1fr 1fr' }}
      >
        {/* Row 1: Low Potential (y=1) */}
        <div
          className="text-[10px] text-gray-400 flex items-center justify-center"
          style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}
        >
          {yLabels[0]}
        </div>
        {renderCell(1, 1)}
        {renderCell(2, 1)}
        {renderCell(3, 1)}

        {/* Row 2: Medium Potential (y=2) */}
        <div
          className="text-[10px] text-gray-400 flex items-center justify-center"
          style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}
        >
          {yLabels[1]}
        </div>
        {renderCell(1, 2)}
        {renderCell(2, 2)}
        {renderCell(3, 2)}

        {/* Row 3: High Potential (y=3) */}
        <div
          className="text-[10px] text-gray-400 flex items-center justify-center"
          style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}
        >
          {yLabels[2]}
        </div>
        {renderCell(1, 3)}
        {renderCell(2, 3)}
        {renderCell(3, 3)}
      </div>


    </div>
  );
};

export default NineBoxMatrixWidget;
